import { useEffect } from 'react';
import { AdminShell, SessionBar, applyRemoteCatalog, useDensity, useInbox } from '@chat-rea/console-ui';
import { API_BASE, API_BASE_SOURCE, IS_MOCK, client } from './api';
import { useConsoleActions } from './hooks/useConsoleActions';
import { useHashRoute } from './hooks/useHashRoute';
import { useIsNarrow } from './hooks/useIsNarrow';
import { useAuth, useRelogin } from './auth/AuthGate';
import { DENIED_FALLBACK, canAccessSection, canAccessSystemTab, routeToUrl, type SystemTab } from './shell/routes';
import { ConsoleShell, SECTION_LABEL, SYSTEM_NAV, type Section } from './shell/ConsoleShell';
import { Forbidden, type ForbiddenRole } from './shell/Forbidden';
import { conversationFooterRole } from './shell/shell-copy';
import { AiObservabilityPage } from './pages/AiObservabilityPage';
import { InboxLayout } from './shell/InboxLayout';
import { AccountsPage } from './pages/AccountsPage';
import { SugargirlPanel } from './sg/SugargirlPanel';
import { useSugargirl } from './sg/useSugargirl';
import { WaterfallPage } from './pages/control/WaterfallPage';
import { MonitorPage } from './pages/control/MonitorPage';
import { RoomsMapPage } from './pages/control/RoomsMapPage';
import { ParamsPage } from './pages/control/ParamsPage';
import { GrantsPage } from './pages/control/GrantsPage';
import { InvitesPage } from './pages/control/InvitesPage';

/**
 * 收件匣工作台（外殼＝@tpte/ui 的 AppShell，內容＝三欄）。
 *
 * 這個檔只剩「接線」，刻意保持很薄：
 * 1. `useInbox` **必須在這裡、在 App 直下**呼叫一次。它是全站唯一的 `client.subscribe()`；
 *    放進任何會被條件渲染／重新掛載的元件裡（外殼、主題 provider、分頁容器）就會重新訂閱，
 *    下場是漏訊息或同一則進兩份 state（PLAN R3）。
 * 2. 動作與面板狀態在 `useConsoleActions`，畫面在 `shell/`。
 * 3. 「目前在哪一頁」的唯一來源是網址 hash（`hooks/useHashRoute` ＋ `shell/routes.ts`）：
 *    `section`／`systemTab`／目前對話 id 都從網址推導，切頁不會動到任何訂閱，
 *    重整、貼網址、按上一頁都停在同一頁（#01，不引入 router 套件）。
 * 4. **四區（T01）**：工作台（`workspace`）／身分（`identities`）／房間（`rooms`、`sg`）／
 *    系統（`system`）。系統內部的四段是網址的第二段（`#/system/<tab>`），只有內容被換掉，
 *    `useInbox` 與 `useConsoleActions` 都不受影響（DESIGN-admin §6.3）。
 * 5. 舊網址（`#/accounts`、`#/admin/sessions`、`#/health`、`#/ai`…）在 `parseRoute` 就被
 *    正規化成新形式，這裡不再有第二套 alias 分支——少一個會腐爛的地方。
 */
export function App() {
  // 唯一一份訂閱：位置比任何外觀元件都外層，切 section／密度／主題都不會重新掛載。
  const inbox = useInbox({ client });
  /**
   * 站清單（#09）：先問後端 `GET /api/platforms`，有回就以它為準；
   * 沒有這支端點就沿用 `platforms.ts` 的本地目錄。**加站不用改前端**。
   */
  useEffect(() => {
    let cancelled = false;
    void client
      .listPlatforms?.()
      .then((rows) => {
        if (!cancelled) applyRemoteCatalog(rows);
      })
      .catch(() => {
        /* 端點不存在＝沿用本地目錄，不是錯誤 */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const narrow = useIsNarrow();
  const c = useConsoleActions(inbox, narrow);
  const density = useDensity();
  /**
   * SG 頁的資料。**在這裡（App 直下）呼叫**，和 `useInbox` 同一個理由：切換視圖時不要重掛，
   * 否則每次切回來都要重新抓一輪草稿（使用者會看到 AI 回覆「閃一下又不見」）。
   */
  const sg = useSugargirl();

  /**
   * 目前視圖全部從網址來：`section`／`systemTab`／`sessionId`。
   * 沒有第二份 state 可以不同步；瀏覽器上一頁／F5／貼連結都走同一條解析。
   */
  const { route, navigate } = useHashRoute();
  const section = route.section;
  const systemTab = route.systemTab;
  const { conversations, activeId, mobileThread, openConversation, inboxLoading } = c;
  /** 手機是「清單 or 對話」兩層，桌機永遠看得到對話串。 */
  const showThread = narrow ? mobileThread : true;

  /**
   * 網址 → 選取：貼上 `#/inbox/<id>` 或按上一頁時，把那一條對話打開。
   * 清單還沒載到就先等（`conversations` 是依賴），載到了再對一次；
   * 找不到該 id（對話已刪）就讓下面那個 effect 把網址收斂回 `#/inbox`。
   */
  useEffect(() => {
    if (section !== 'workspace') return;
    const wanted = route.sessionId;
    if (!wanted || wanted === activeId) return;
    if (!conversations.some((item) => item.id === wanted)) return;
    openConversation(wanted);
  }, [section, route.sessionId, activeId, conversations, openConversation]);

  /**
   * 選取 → 網址：點清單另一列、手機按返回，都改成對應網址。
   * `replace` 是刻意的——同一頁裡換一條對話不該讓「上一頁」多一格（上一頁要回前一頁面）。
   * 若網址上還帶著一個「尚未兌現」的 id（重整中、清單未到），先不要覆蓋它。
   */
  const pendingDeepLink =
    route.sessionId !== null &&
    route.sessionId !== activeId &&
    (inboxLoading || conversations.some((item) => item.id === route.sessionId));
  useEffect(() => {
    if (section !== 'workspace' || pendingDeepLink) return;
    const next = { ...route, section: 'workspace' as const, sessionId: showThread ? activeId : null };
    if (routeToUrl(next) !== window.location.hash) navigate(next, { replace: true });
  }, [section, route, showThread, activeId, pendingDeepLink, navigate]);

  /**
   * 一級區切換。系統區預設落在目前的分頁（從別區進來才回總覽），
   * 免得「在系統的 ops 段按側欄系統」被丟回總覽。
   */
  const handleSectionChange = (next: Section) => {
    if (next === 'system') {
      navigate({
        ...route,
        section: 'system',
        systemTab: section === 'system' ? systemTab : 'overview',
        sessionId: null,
      });
      return;
    }
    if (next === 'workspace') {
      // 回工作台時把還記得的選取帶回網址，重整後不會退回空白清單。
      navigate({ ...route, section: 'workspace', sessionId: showThread ? activeId : null });
      return;
    }
    navigate({ ...route, section: next, sessionId: null });
  };

  const handleSystemTabChange = (tab: SystemTab) => {
    navigate({ ...route, section: 'system', systemTab: tab, sessionId: null });
  };

  // 角色只看 auth-service 回來的 roles，前端不硬寫 email。
  const auth = useAuth();
  const canAdmin = auth?.isAdmin ?? false;
  const canEngineer = auth?.isEngineer ?? false;
  const access = { canAdmin, canEngineer };
  const loginRole: ForbiddenRole = canAdmin ? 'admin' : canEngineer ? 'engineer' : 'chatter';
  /**
   * 身分守門（#08）：網址只是「想去哪」，能不能去由 `canAccessSection()` 決定。
   * 聊手：工作台／瀑布／身分；工程師另開系統；房間／SG 仍僅 admin。
   */
  const sectionAllowed = canAccessSection(section, access);
  const tabAllowed = section !== 'system' || canAccessSystemTab(systemTab, access);
  const allowed = sectionAllowed && tabAllowed;
  const blockedAdminTab = section === 'system' && sectionAllowed && !tabAllowed;
  const deniedLabel = blockedAdminTab
    ? (SYSTEM_NAV.find((entry) => entry.tab === systemTab)?.label ?? SECTION_LABEL.system)
    : SECTION_LABEL[section];
  /**
   * 內容區最上方那一列（取代舊的 API Token 列）：顯示目前登入者；
   * 401 時換成「登入已經過期，請重新登入」＋按鈕，按鈕走既有的登出流程回登入頁。
   */
  const relogin = useRelogin();
  /**
   * SessionBar 只留「登入過期」那條（`email: ''` 時它不渲染身分行）。
   * 身分顯示只有一份＝頂列的 `topbar-account`；這裡再顯示一次就是重複。
   */
  const session = {
    email: '',
    roles: auth?.roles ?? [],
    onRelogin: relogin,
  };
  // 被擋下的分區不渲染任何內容（走上面的 403 畫面），外殼仍停在原本那一區，
  // 這樣頂列標題與 403 講的是同一件事，不會出現「標題寫收件匣、內容說沒權限」。
  const shownSection = section;

  /**
   * 頂列第四項（帳號池健康度）：**跟「身分」頁同一份資料**（`useConsoleActions` 在 mount 就打
   * `GET /api/accounts`，不管在哪一頁都只有這一份），所以這裡不再多打一支 API、也不另存 state。
   *
   * 判定只用 `PoolAccount` 已經有的欄位：
   * - 可用 `ok === true`（後端驗過能登入）
   * - 異常 `ok === false`（登入失敗，還留在池子裡）
   * - 受限 `tokenValid === false`（存著的憑證失效；後端多半沒挑這個欄位，沒有就不算）
   *
   * 池子還沒載到 → `loading`（頂列顯示「池 —」，不謊報 0）；一支都沒載回來而且有錯誤 → 整項不顯示。
   */
  const poolHealth =
    c.poolError && c.poolAccounts.length === 0
      ? undefined
      : {
          total: c.poolAccounts.length,
          ok: c.poolAccounts.filter((account) => account.ok).length,
          failing: c.poolAccounts.filter((account) => !account.ok).length,
          expired: c.poolAccounts.filter((account) => account.tokenValid === false).length,
          loading: c.poolLoading,
        };

  return (
    <ConsoleShell
      section={shownSection}
      systemTab={systemTab}
      onSectionChange={handleSectionChange}
      onSystemTabChange={handleSystemTabChange}
      density={density}
      poolHealth={poolHealth}
      canAdmin={canAdmin}
      canEngineer={canEngineer}
      identity={
        c.active
          ? {
              name: c.active.nickname,
              role: conversationFooterRole(c.active),
            }
          : { name: '未選身分', role: conversationFooterRole(null) }
      }
      topbar={{
        conversationCount: c.conversations.length,
        unreadTotal: c.unreadTotal,
        /** 登入者（唯一一份身分顯示）與登出：兩者都走 AuthGate 的既有流程。 */
        account: auth
          ? {
              email: auth.email,
              role: auth.isAdmin ? 'admin' : auth.isEngineer ? 'engineer' : '聊手',
            }
          : undefined,
        onSignOut: relogin,
        drawer:
          section === 'workspace'
            ? { open: c.drawerOpen, onToggle: () => c.setDrawerOpen((open) => !open) }
            : undefined,
        mode: {
          isMock: IS_MOCK,
          title: IS_MOCK
            ? `mock（來源：${API_BASE_SOURCE}；在 /config.json 設 apiBase 即走真後端）`
            : `${API_BASE}（來源：${API_BASE_SOURCE}）`,
        },
      }}
    >
      {!allowed ? (
        <Forbidden
          page={deniedLabel}
          reason={blockedAdminTab ? 'admin-tab' : 'section'}
          loginRole={loginRole}
          onBack={() => navigate(DENIED_FALLBACK, { replace: true })}
        />
      ) : shownSection === 'waterfall' ? (
        <WaterfallPage
          linkedSessionId={route.sessionId}
          linkedPlatform={route.platform}
          onSelectSession={(sessionId) => {
            navigate({ ...route, section: 'waterfall', sessionId }, { replace: true });
          }}
          onPlatformChange={(platform) => {
            navigate({ ...route, section: 'waterfall', platform }, { replace: true });
          }}
        />
      ) : shownSection === 'system' && (canAdmin || canEngineer) ? (
        /* 系統：共用一個殼（回鏈 ＋ tab 列）。控制面三頁與 AI 成效由宿主注入。 */
        <AdminShell
          tab={systemTab}
          onTabChange={(tab) => handleSystemTabChange(tab)}
          canAdmin={canAdmin}
          staffSession={canAdmin || canEngineer}
          client={client}
          session={session}
          aiContent={<AiObservabilityPage />}
          controlContent={{
            monitor: <MonitorPage />,
            'rooms-map': <RoomsMapPage />,
            params: <ParamsPage />,
            grants: canAdmin ? <GrantsPage /> : <p className="p-3 text-sm">需要管理者</p>,
            invites: canAdmin ? <InvitesPage /> : <p className="p-3 text-sm">需要管理者</p>,
          }}
          onBack={() => handleSectionChange('workspace')}
          unreadLive={{
            total: c.unreadTotal,
            sessions: c.conversations.length,
            loading: c.inboxLoading,
          }}
        />
      ) : shownSection === 'sg' ? (
        /* SG 頁自成一頁：上面放登入身分列（console 走 PIN Bearer session，不再貼 token），
           下面才是女孩／session 面板。機器對機器的 sga_ 金鑰／SERVICE_TOKEN 由外站自己帶，與這裡無關。 */
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-2 px-3 pt-3">
            <SessionBar {...session} />
          </div>
          <SugargirlPanel sg={sg} />
        </>
      ) : shownSection === 'identities' ? (
        /* 身分單一入口（`#/identities`；舊 `#/accounts`、`#/accounts/sessions|creds`、
           `#/admin/accounts` 都解析到這裡並被正規化）。不再走 `InboxLayout` 的整頁面板分支，
           舊的「已開身分／憑證池」兩個分頁面版就此下線（元件先留著，T05 收掉）。 */
        <AccountsPage
          c={c}
          session={session}
          viewer={auth ? { email: auth.email, role: auth.isAdmin ? 'admin' : '聊手' } : undefined}
        />
      ) : (
        <InboxLayout
          c={c}
          narrow={narrow}
          section={shownSection}
          isAdmin={canAdmin}
          session={session}
        />
      )}
    </ConsoleShell>
  );
}
